/**
 * QuizModal.tsx — M-1 SDG Trivia Quiz minigame.
 *
 * Shown on Event tile when newsgirl offers a quiz (pendingQuiz !== null).
 * Gated on boardAnimationActive === false (R11.2 invariant).
 *
 * VISIBLE WHEN: pendingQuiz !== null AND boardAnimationActive === false
 * TEST PATH: roll dice → land on Event tile → newsgirl offerQuiz() → QuizModal renders.
 *
 * R13 Bug #9 — phase machine (3 phases):
 *   1. "offer"    — conversation overlay: newsgirl pitch → "Take Quiz" | "Skip to News"
 *   2. "question" — 30s timed question (existing logic, timer now stops on answer)
 *   3. "reveal"   — no timer, leisure read of answer + explanation + "Draw News Card" button
 *
 * Skip path: "Skip to News" closes modal via skipQuiz() then triggers triggerEventCardDraw().
 * Draw News Card: closes quiz and immediately calls triggerEventCardDraw().
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import quizBankJson from "../../../../../data/quiz-bank.json";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./QuizModal.module.css";

// ---------------------------------------------------------------------------
// Quiz question type (mirrors quiz-bank.json schema)
// ---------------------------------------------------------------------------

interface QuizQuestion {
  id: string;
  sdg: number;
  question: string;
  choices: [string, string, string, string];
  correctIdx: number;
  explanation: string;
}

const QUIZ_QUESTIONS = quizBankJson.questions as unknown as QuizQuestion[];

const QUIZ_TIMEOUT_MS = 30_000; // 30s

// R15 carry-forward (R13 /goal Risk 1): rotate newsgirl offer speech so repeat
// quizzes don't feel canned. Keyed off question index for determinism per
// question (same question idx → same speech).
const OFFER_SPEECHES = [
  "Hey traveler! Want to test your SDG knowledge?",
  "Got a quick one for ya — care to guess?",
  "Stop the press! Quiz time before today's headline.",
  "Smart-news exclusive: think you know the answer?",
  "Cub reporter needs a hand — wanna take a stab?",
  "One question, one minute. Game?",
] as const;

function pickOfferSpeech(questionIdx: number): string {
  return OFFER_SPEECHES[questionIdx % OFFER_SPEECHES.length] ?? OFFER_SPEECHES[0];
}

// ---------------------------------------------------------------------------
// Countdown ring SVG component
// ---------------------------------------------------------------------------

interface CountdownRingProps {
  totalMs: number;
  remainingMs: number;
  /** Ring diameter in px */
  size?: number;
}

function CountdownRing({
  totalMs,
  remainingMs,
  size = 48,
}: CountdownRingProps): React.ReactElement {
  const r = (size - 6) / 2;
  const circumference = 2 * Math.PI * r;
  const progress = Math.max(0, Math.min(1, remainingMs / totalMs));
  const offset = circumference * (1 - progress);
  const color = remainingMs < 10_000 ? "#ea5656" : remainingMs < 20_000 ? "#ffb420" : "#5bc07a";
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      className={styles.countdownRing}
    >
      {/* Track */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={5}
      />
      {/* Progress arc — starts at top (rotated -90°) */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={5}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
      />
      {/* Seconds label */}
      <text
        x={size / 2}
        y={size / 2 + 5}
        textAnchor="middle"
        fill={color}
        fontSize={14}
        fontFamily="'VT323', monospace"
      >
        {Math.ceil(remainingMs / 1000)}
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Quiz modal phase machine
// ---------------------------------------------------------------------------

type QuizPhase = "offer" | "question" | "reveal";
type RevealState = "answered-correct" | "answered-wrong" | null;

// ---------------------------------------------------------------------------
// Main QuizModal — outer gate + keying
// ---------------------------------------------------------------------------

export function QuizModal(): React.ReactElement | null {
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const pendingQuiz = useGameStore((s) => s.pendingQuiz);
  const answerQuiz = useGameStore((s) => s.answerQuiz);
  const skipQuiz = useGameStore((s) => s.skipQuiz);
  const triggerEventCardDraw = useGameStore((s) => s.triggerEventCardDraw);

  // R13 Codex-B HIGH 1+2: latch quiz locally so the reveal phase survives
  // pendingQuiz being cleared by answerQuiz(). Inner controls unmount via
  // onComplete callback (called by "Draw News Card" / "Skip to News").
  const [activeQuestionIdx, setActiveQuestionIdx] = useState<number | null>(null);

  useEffect(() => {
    if (pendingQuiz !== null && activeQuestionIdx === null) {
      setActiveQuestionIdx(pendingQuiz.questionIdx);
    }
  }, [pendingQuiz, activeQuestionIdx]);

  if (boardAnimationActive) return null;
  if (activeQuestionIdx === null) return null;

  const question = QUIZ_QUESTIONS[activeQuestionIdx];
  if (question === undefined) return null;

  return (
    <QuizModalInner
      key={`quiz-${activeQuestionIdx}`}
      question={question}
      questionIdx={activeQuestionIdx}
      answerQuiz={answerQuiz}
      skipQuiz={skipQuiz}
      triggerEventCardDraw={triggerEventCardDraw}
      onComplete={() => setActiveQuestionIdx(null)}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner (keyed so hooks reset cleanly when questionIdx changes)
// ---------------------------------------------------------------------------

interface InnerProps {
  question: QuizQuestion;
  questionIdx: number;
  answerQuiz: (choiceIdx: number) => void;
  skipQuiz: () => void;
  triggerEventCardDraw: () => void;
  onComplete: () => void;
}

function QuizModalInner({
  question,
  questionIdx,
  answerQuiz,
  skipQuiz,
  triggerEventCardDraw,
  onComplete,
}: InnerProps): React.ReactElement {
  const { t } = useI18n();
  const [phase, setPhase] = useState<QuizPhase>("offer");
  const [remainingMs, setRemainingMs] = useState(QUIZ_TIMEOUT_MS);
  const [revealState, setRevealState] = useState<RevealState>(null);
  const [pickedIdx, setPickedIdx] = useState<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  const timerStopped = revealState !== null;

  // ── Countdown ticker — only runs in "question" phase and not stopped ──────
  useEffect(() => {
    if (phase !== "question" || timerStopped) return;
    const start = Date.now();
    intervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, QUIZ_TIMEOUT_MS - elapsed);
      setRemainingMs(remaining);
      if (remaining <= 0) {
        window.clearInterval(intervalRef.current ?? 0);
        setRevealState("answered-wrong");
        setPickedIdx(-1);
        // Credit check: -1 never matches correctIdx → no reward.
        // answerQuiz clears pendingQuiz, but we stay in "reveal" phase until
        // "Draw News Card" is clicked — so call it immediately to credit/debit.
        answerQuiz(-1);
        setPhase("reveal");
      }
    }, 100);
    return () => {
      window.clearInterval(intervalRef.current ?? 0);
    };
  }, [phase, timerStopped, answerQuiz]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      window.clearInterval(intervalRef.current ?? 0);
    };
  }, []);

  // ── Offer phase handlers ──────────────────────────────────────────────────

  const handleTakeQuiz = useCallback(() => {
    if (phase !== "offer") return;
    setPhase("question");
    setRemainingMs(QUIZ_TIMEOUT_MS);
  }, [phase]);

  const handleSkipToNews = useCallback(() => {
    if (phase !== "offer") return;
    skipQuiz(); // clears pendingQuiz
    triggerEventCardDraw(); // proceeds to news-card draw flow
    onComplete(); // unmount QuizModal so news card flow can show
  }, [phase, skipQuiz, triggerEventCardDraw, onComplete]);

  // ── Question phase handler ────────────────────────────────────────────────

  const handlePick = useCallback(
    (idx: number) => {
      if (phase !== "question" || timerStopped) return;
      window.clearInterval(intervalRef.current ?? 0);
      const correct = idx === question.correctIdx;
      setPickedIdx(idx);
      setRevealState(correct ? "answered-correct" : "answered-wrong");
      // Credit immediately — pendingQuiz will be null after this but QuizModal
      // stays mounted in "reveal" phase until "Draw News Card" is clicked.
      answerQuiz(idx);
      setPhase("reveal");
    },
    [phase, timerStopped, question.correctIdx, answerQuiz],
  );

  // ── Reveal phase handler ──────────────────────────────────────────────────

  const handleDrawNewsCard = useCallback(() => {
    // pendingQuiz is already null (cleared by answerQuiz/skipQuiz).
    // Trigger the event-card draw flow then unmount via onComplete.
    triggerEventCardDraw();
    onComplete();
  }, [triggerEventCardDraw, onComplete]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: overlay <div> needed for z-index layering; <dialog> resets stacking context
      role="dialog"
      aria-modal="true"
      aria-label="SDG Trivia Quiz"
      data-testid="quiz-modal"
    >
      <div className={styles.panel}>
        {/* ── OFFER PHASE ─────────────────────────────────────────────────── */}
        {phase === "offer" && (
          <>
            <div className={styles.header}>
              <span className={styles.sdgBadge} data-testid="quiz-sdg-badge">
                SDG {question.sdg}
              </span>
              <span className={styles.headerTitle}>{t("quiz.npcName")}</span>
            </div>

            <div className={styles.offerBody} data-testid="quiz-offer-body">
              <div className={styles.offerNpcRow}>
                <span className={styles.offerNpcIcon}>📰</span>
                <p className={styles.offerSpeech} data-testid="quiz-offer-speech">
                  {pickOfferSpeech(questionIdx)} <strong>+15 tokens</strong> if you guess right. Or
                  skip ahead to read today's headline.
                </p>
              </div>
            </div>

            <div className={styles.offerActions}>
              <button
                type="button"
                className={styles.btnTakeQuiz}
                onClick={handleTakeQuiz}
                data-testid="quiz-take-quiz-btn"
              >
                Take Quiz
              </button>
              <button
                type="button"
                className={styles.btnSkipToNews}
                onClick={handleSkipToNews}
                data-testid="quiz-skip-to-news-btn"
              >
                Skip to News
              </button>
            </div>
          </>
        )}

        {/* ── QUESTION PHASE ──────────────────────────────────────────────── */}
        {phase === "question" && (
          <>
            <div className={styles.header}>
              <span className={styles.sdgBadge} data-testid="quiz-sdg-badge">
                SDG {question.sdg}
              </span>
              <span className={styles.headerTitle}>{t("quiz.questionTitle")}</span>
              <CountdownRing totalMs={QUIZ_TIMEOUT_MS} remainingMs={remainingMs} />
            </div>

            <p className={styles.question} data-testid="quiz-question">
              {question.question}
            </p>

            <p className={styles.rewardHint} aria-hidden="true">
              +15 tokens if correct
            </p>

            <div className={styles.choices} data-testid="quiz-choices">
              {question.choices.map((choice, idx) => (
                <button
                  key={choice}
                  type="button"
                  className={styles.choiceBtn}
                  onClick={() => handlePick(idx)}
                  disabled={false}
                  data-testid={`quiz-choice-${idx}`}
                  title={choice}
                >
                  {choice}
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── REVEAL PHASE ────────────────────────────────────────────────── */}
        {phase === "reveal" && (
          <>
            <div className={styles.header}>
              <span className={styles.sdgBadge} data-testid="quiz-sdg-badge">
                SDG {question.sdg}
              </span>
              <span className={styles.headerTitle}>{t("quiz.resultTitle")}</span>
              {/* No timer in reveal phase — leisure read */}
            </div>

            {revealState === "answered-correct" && (
              <div
                className={styles.revealCorrect}
                data-testid="quiz-reveal-correct"
                aria-live="polite"
              >
                ✓ Correct! +15 tokens
              </div>
            )}
            {revealState === "answered-wrong" && (
              <div
                className={styles.revealWrong}
                data-testid="quiz-reveal-wrong"
                aria-live="polite"
              >
                ✗ Correct answer: {question.choices[question.correctIdx]}
              </div>
            )}

            {/* Explanation — leisure read, no time pressure */}
            <p className={styles.explanation} data-testid="quiz-explanation">
              {question.explanation}
            </p>

            {/* Dimmed choice summary so player can see which was right */}
            <div className={styles.choices} data-testid="quiz-choices">
              {question.choices.map((choice, idx) => {
                let choiceClass = `${styles.choiceBtn} ${styles.choiceBtnDimmed}`;
                if (idx === question.correctIdx) {
                  choiceClass = `${styles.choiceBtn} ${styles.choiceBtnCorrect}`;
                } else if (idx === pickedIdx && pickedIdx !== question.correctIdx) {
                  choiceClass = `${styles.choiceBtn} ${styles.choiceBtnWrong}`;
                }
                return (
                  <button
                    key={choice}
                    type="button"
                    className={choiceClass}
                    disabled
                    data-testid={`quiz-choice-${idx}`}
                    title={choice}
                  >
                    {choice}
                  </button>
                );
              })}
            </div>

            {/* R15 carry-forward (R13 /goal Risk 2): clearer button label —
                "Read Today's Headline" explains what the player gets next,
                rather than the unclear "Draw News Card". Same testid. */}
            <button
              type="button"
              className={styles.btnDrawNewsCard}
              onClick={handleDrawNewsCard}
              data-testid="quiz-draw-news-card-btn"
            >
              📰 Read Today's Headline →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
